import { useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import { ChevronDownIcon, ShoppingCartIcon } from '@heroicons/react/24/outline'
import { equipmentOptions } from 'functions/equipmentUtils'
import { GetPrimaryColorHex } from 'redux/slices/brand'
import { ModalContext } from '../ModalContext'

import BrandColorButton from 'components/Buttons/BrandColorButton'
import WeightAvailabilitySection from 'components/Equipment/WeightAvailabilitySection'

export default function EditEquipmentProfileModal() {

    const { modalData, HideModal } = useContext(ModalContext)

    const styleConfig = useSelector((state) => state.brand.styleConfig)

    const [equipmentProfileJSON, setEquipmentProfileJSON] = useState(() => {
        if (modalData.equipmentProfileJSON == null) return null
        return JSON.parse(JSON.stringify(modalData.equipmentProfileJSON))
    })
    const [name, setName] = useState(modalData.equipmentProfileName ?? '')
    const [description, setDescription] = useState(modalData.equipmentProfileDescription ?? '')
    const [errorMessage, setErrorMessage] = useState('')
    const [saveLoading, setSaveLoading] = useState(false)

    const [activateOnSave, setActivateOnSave] = useState(false)

    const displayNameAndDescription = modalData.displayNameAndDescription === true
    const displayActivateOnSaveToggle = modalData.displayActivateOnSaveToggle === true
    const [isEquipmentListExpanded, setIsEquipmentListExpanded] = useState(false)
    const [isPlateExpanded, setIsPlateExpanded] = useState(false)
    const [isDumbbellExpanded, setIsDumbbellExpanded] = useState(false)
    const [isKettlebellExpanded, setIsKettlebellExpanded] = useState(false)

    const max_plate_quantity = 4
    const max_dumbbell_quantity = 2
    const max_kettlebell_quantity = 2

    const mutateEquipmentProfile = (mutationKey, equipmentKey, index = 0) => {
        if (equipmentProfileJSON == null) return

        const updatedProfile = JSON.parse(JSON.stringify(equipmentProfileJSON))

        switch (mutationKey) {
            case 'toggle-base-equipment':
                updatedProfile.baseEquipment[equipmentKey] = !updatedProfile.baseEquipment[equipmentKey]
                break

            case 'increment-weight-availability':
                switch (equipmentKey) {
                    case 'plate':
                        if (updatedProfile.plateAvailability[index].quantity < max_plate_quantity) {
                            updatedProfile.plateAvailability[index].quantity += 1
                        }
                        break

                    case 'dumbbell':
                        if (updatedProfile.dumbbellAvailability[index].quantity < max_dumbbell_quantity) {
                            updatedProfile.dumbbellAvailability[index].quantity += 1
                        }
                        break

                    case 'kettlebell':
                        if (updatedProfile.kettlebellAvailability[index].quantity < max_kettlebell_quantity) {
                            updatedProfile.kettlebellAvailability[index].quantity += 1
                        }
                        break
                }
                break

            case 'decrement-weight-availability':
                switch (equipmentKey) {
                    case 'plate':
                        if (updatedProfile.plateAvailability[index].quantity > 0) {
                            updatedProfile.plateAvailability[index].quantity -= 1
                        }
                        break

                    case 'dumbbell':
                        if (updatedProfile.dumbbellAvailability[index].quantity > 0) {
                            updatedProfile.dumbbellAvailability[index].quantity -= 1
                        }
                        break

                    case 'kettlebell':
                        if (updatedProfile.kettlebellAvailability[index].quantity > 0) {
                            updatedProfile.kettlebellAvailability[index].quantity -= 1
                        }
                        break
                }
                break

            case 'add-all-weight-availability':
                switch (equipmentKey) {
                    case 'plate':
                        updatedProfile.plateAvailability.forEach(item => {
                            item.quantity = max_plate_quantity
                        })
                        break

                    case 'dumbbell':
                        updatedProfile.dumbbellAvailability.forEach(item => {
                            item.quantity = max_dumbbell_quantity
                        })
                        break

                    case 'kettlebell':
                        updatedProfile.kettlebellAvailability.forEach(item => {
                            item.quantity = max_kettlebell_quantity
                        })
                        break
                }
                break

            case 'remove-all-weight-availability':
                switch (equipmentKey) {
                    case 'plate':
                        updatedProfile.plateAvailability.forEach(item => {
                            item.quantity = 0
                        })
                        break

                    case 'dumbbell':
                        updatedProfile.dumbbellAvailability.forEach(item => {
                            if (item.weight <= 20) {
                                item.quantity = 2
                            } else {
                                item.quantity = 0
                            }
                        })
                        break

                    case 'kettlebell':
                        updatedProfile.kettlebellAvailability.forEach(item => {
                            item.quantity = 0
                        })
                        break
                }
                break
        }

        setEquipmentProfileJSON(updatedProfile)
    }

    const OnSave = async () => {
        if (equipmentProfileJSON == null) return

        if (displayNameAndDescription && name.trim().length === 0) {
            setErrorMessage('Name is required')
            return
        }

        setErrorMessage('')
        setSaveLoading(true)

        // buttonAction decides what saving means (e.g. update local state or save to the backend). Returning false keeps the modal open

        if (typeof modalData.buttonAction === 'function') {
            const result = await modalData.buttonAction(equipmentProfileJSON, { name: name.trim(), description: description, activateOnSave: activateOnSave })

            if (result === false) {
                setSaveLoading(false)
                setErrorMessage('There was an error saving the equipment profile. Please try again.')
                return
            }
        }

        setSaveLoading(false)

        HideModal()
    }

    if (equipmentProfileJSON == null) return null

    const activeEquipmentCount = equipmentOptions.filter(equipment => (
        Object.keys(equipmentProfileJSON.baseEquipment).includes(equipment.key) === false
        || equipmentProfileJSON.baseEquipment[equipment.key]
    )).length

    return (
        <div className="flex h-[65vh] flex-col gap-4 text-left">
            <div className="shrink-0 text-center text-lg font-semibold leading-6 text-gray-900">
                {modalData.titleText ?? 'Edit Equipment Profile'}
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {displayNameAndDescription && (
                    <>
                        <div className="space-y-1.5">
                            <label htmlFor="equipmentProfileName" className="block text-sm font-semibold text-gray-800">Name</label>
                            <input
                                id="equipmentProfileName"
                                type="text"
                                placeholder="e.g. Home Gym"
                                className="block w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-400 focus:border-transparent"
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label htmlFor="equipmentProfileDescription" className="block text-sm font-semibold text-gray-800">
                                Description <span className="font-normal text-gray-500">(optional)</span>
                            </label>
                            <textarea
                                id="equipmentProfileDescription"
                                rows={2}
                                placeholder="Where or when this equipment is available"
                                className="block w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-400 focus:border-transparent resize-none"
                                value={description}
                                onChange={(event) => setDescription(event.target.value)}
                            />
                        </div>
                    </>
                )}

                <div className="bg-gray-100 rounded-lg overflow-hidden">
                    <div
                        className="flex cursor-pointer items-center justify-between px-6 py-4"
                        onClick={() => setIsEquipmentListExpanded(!isEquipmentListExpanded)}
                    >
                        <div className="flex items-center gap-x-2">
                            <span className="text-base font-semibold text-gray-800">Equipment Availability</span>

                            <span className="text-xs text-gray-500">
                                ({activeEquipmentCount} of {equipmentOptions.length})
                            </span>
                        </div>

                        <ChevronDownIcon
                            className={`
                                h-5 w-5 text-gray-500 transform transition-transform duration-300 ease-out
                                ${isEquipmentListExpanded ? 'rotate-180' : ''}
                            `}
                        />
                    </div>

                    <div
                        className={`
                            grid transition-[grid-template-rows] duration-300 ease-out
                            ${isEquipmentListExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
                        `}
                    >
                        <div className="min-h-0 overflow-hidden">
                            <div
                                className={`
                                    divide-y divide-gray-300 border-t border-gray-300 transition-opacity duration-200 ease-out
                                    ${isEquipmentListExpanded ? 'opacity-100' : 'pointer-events-none opacity-0'}
                                `}
                            >
                                {equipmentOptions.map((equipment, index) => (
                                    <div
                                        key={index}
                                        className="flex cursor-pointer items-center p-4"
                                        onClick={() => {
                                            if (Object.keys(equipmentProfileJSON.baseEquipment).includes(equipment.key)) {
                                                mutateEquipmentProfile('toggle-base-equipment', equipment.key)
                                            }
                                        }}
                                    >
                                        <div className="flex-1 flex items-center gap-x-3">
                                            <img src={equipment.imageURL} alt="Equipment Icon" className="h-8 w-8" />

                                            <div>
                                                <div className="flex items-center gap-x-2">
                                                    <span className="text-sm">{equipment.name}</span>
                                                </div>
                                                <div className="text-xs text-gray-500">{equipment.description}</div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-x-4">
                                            {Object.keys(equipmentProfileJSON.baseEquipment).includes(equipment.key) &&
                                                <div
                                                    className="w-8 h-4 rounded-full p-0.5 transition-colors duration-200 ease-in-out"
                                                    style={{
                                                        backgroundColor: equipmentProfileJSON.baseEquipment[equipment.key] ? GetPrimaryColorHex(styleConfig) : '#e5e7eb'
                                                    }}
                                                >
                                                    <div
                                                        className={`w-3 h-3 rounded-full bg-white transform transition-transform duration-200 ease-in-out ${
                                                            equipmentProfileJSON.baseEquipment[equipment.key] ? 'translate-x-4' : 'translate-x-0'
                                                        }`}
                                                    />
                                                </div>
                                            }

                                            {!Object.keys(equipmentProfileJSON.baseEquipment).includes(equipment.key) &&
                                                <div
                                                    className="w-8 h-4 rounded-full p-0.5 transition-colors duration-200 ease-in-out opacity-25"
                                                    style={{
                                                        backgroundColor: GetPrimaryColorHex(styleConfig)
                                                    }}
                                                >
                                                    <div className="w-3 h-3 rounded-full bg-white transform translate-x-4" />
                                                </div>
                                            }

                                            <div
                                                className="text-gray-500 rounded-md p-2"
                                                onClick={(e) => { e.stopPropagation(); window.open(equipment.shoppingLink, '_blank') }}
                                            >
                                                <ShoppingCartIcon className="h-5 w-5" />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <WeightAvailabilitySection
                    equipmentKey="plate"
                    title="Plate Availability"
                    iconUrl="https://which-weight-public.s3.us-east-2.amazonaws.com/equipment_icons/barbell.png"
                    availabilityJSON={equipmentProfileJSON.plateAvailability}
                    maxQuantity={max_plate_quantity}
                    mutateEquipmentProfile={mutateEquipmentProfile}
                    isExpanded={isPlateExpanded}
                    onToggleExpand={() => setIsPlateExpanded(!isPlateExpanded)}
                    animated={true}
                />

                <WeightAvailabilitySection
                    equipmentKey="dumbbell"
                    title="Dumbbell Availability"
                    iconUrl="https://which-weight-public.s3.us-east-2.amazonaws.com/equipment_icons/dumbbell.png"
                    availabilityJSON={equipmentProfileJSON.dumbbellAvailability}
                    maxQuantity={max_dumbbell_quantity}
                    mutateEquipmentProfile={mutateEquipmentProfile}
                    isExpanded={isDumbbellExpanded}
                    onToggleExpand={() => setIsDumbbellExpanded(!isDumbbellExpanded)}
                    animated={true}
                />

                <WeightAvailabilitySection
                    equipmentKey="kettlebell"
                    title="Kettlebell Availability"
                    iconUrl="https://which-weight-public.s3.us-east-2.amazonaws.com/equipment_icons/kettlebell.png"
                    availabilityJSON={equipmentProfileJSON.kettlebellAvailability}
                    maxQuantity={max_kettlebell_quantity}
                    mutateEquipmentProfile={mutateEquipmentProfile}
                    isExpanded={isKettlebellExpanded}
                    onToggleExpand={() => setIsKettlebellExpanded(!isKettlebellExpanded)}
                    animated={true}
                />
            
                {displayActivateOnSaveToggle && (
                    <div
                        className="flex cursor-pointer items-center justify-between rounded-lg bg-gray-100 px-6 py-4"
                        onClick={() => setActivateOnSave(!activateOnSave)}
                    >
                        <div>
                            <p className="text-sm font-semibold text-gray-800">Activate on save</p>
                            <p className="text-xs text-gray-500">Make this the active equipment profile used to generate workouts</p>
                        </div>

                        <div
                            className="w-8 h-4 rounded-full p-0.5 transition-colors duration-200 ease-in-out"
                            style={{ backgroundColor: activateOnSave ? GetPrimaryColorHex(styleConfig) : '#e5e7eb' }}
                        >
                            <div className={`w-3 h-3 rounded-full bg-white transform transition-transform duration-200 ease-in-out ${activateOnSave ? 'translate-x-4' : 'translate-x-0'}`} />
                        </div>
                    </div>
                )}
            </div>

            {errorMessage !== '' && <p className="shrink-0 text-center text-sm text-red-600">{errorMessage}</p>}

            <BrandColorButton
                text="Save"
                action={OnSave}
                isLoading={saveLoading}
            />
        </div>
    )
}
